<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useProjects, useProjectsReadiness, useProjectsWorkflowCounts, useProjectsLastRuns, useInvalidateProjects, useImportProject, useDeleteProject, type ProjectExportBundleView } from "@/queries/projects";
import { useProducts, useSubscribedProducts, useInvalidateProducts } from "@/queries/products";
import { useAlertStore } from "@/stores/alerts-store";
import { CUSTOM_PRODUCT_KEY } from "@/views/shared/composables/useNavigation";
import ConvertToProductModal from "./modals/ConvertToProductModal.vue";
import DeleteProjectModal from "./modals/DeleteProjectModal.vue";
import DataView from "@/components/DataView.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import type { Project } from "@/types";
import type {Config} from "datatables.net";
import { getProjectStatus as resolveProjectStatus, getProjectStatusClass } from "../utils/projectStatus";


const router = useRouter();
const route = useRoute();
const alertStore = useAlertStore();
const { projects, projectsLoading: loading, projectsError: error, refetchProjects } = useProjects();
// Wizard status loads in parallel with the project list. The list doesn't wait for it —
// rows paint immediately with no badge; the "Setup Required" badge appears for incomplete
// projects when this resolves a moment later.
const { readinessByProject } = useProjectsReadiness();
// Live workflow counts — bypasses the broken `project.workflowCount` field on the list response.
// Same parallel-endpoint pattern as readiness-status; the list paints immediately and each card's
// workflow count fills in once this resolves.
const { workflowCountsByProject } = useProjectsWorkflowCounts();
const { lastRunByProject } = useProjectsLastRuns();
const { products } = useProducts();
const { subscribedProducts } = useSubscribedProducts();
const invalidateProjects = useInvalidateProjects();
const invalidateProducts = useInvalidateProducts();
const { importProjectAsync, importProjectPending, importProjectError, importProjectReset } = useImportProject();

// Import flow: pick file → parse → confirm. The bundle carries its source product; we just show
// it (read-only) and let the user override the project name if they want.
const showImportModal = ref(false);
const importFileInput = ref<HTMLInputElement | null>(null);
const pendingBundle = ref<ProjectExportBundleView | null>(null);
const importProjectName = ref("");
const importResultMessage = ref<string | null>(null);

// Bundle's product name resolved against the org's product list (purely for display).
const bundleProductName = computed(() => {
  const id = pendingBundle.value?.productId;
  if (!id) return null;
  return products.value.find(p => p.id === id)?.name ?? null;
});

// Org has a legitimate relationship with the bundle's product if it owns the product (publisher)
// OR subscribes to it. The backend enforces the same rule; this is a soft pre-flight hint.
const orgHasAccessToBundleProduct = computed(() => {
  const id = pendingBundle.value?.productId;
  if (!id) return false;
  // products.value contains products owned by the current org — covers the publisher case.
  if (products.value.some(p => p.id === id)) return true;
  // Subscribed products covers the subscriber case.
  return subscribedProducts.value.some(p => p.id === id);
});

function openImportDialog() {
  pendingBundle.value = null;
  importProjectName.value = "";
  importResultMessage.value = null;
  importProjectReset();
  showImportModal.value = true;
}

// Import lives here on the landing page (it needs the full product list to resolve the bundle's
// target). The project-level Import action redirects here with ?import=1 to auto-open the modal,
// saving a second click. Strip the flag afterwards so a refresh doesn't re-pop the dialog.
onMounted(() => {
  if (route.query.import === "1") {
    openImportDialog();
    router.replace({ query: { ...route.query, import: undefined } });
  }
});

function closeImportDialog() {
  if (importProjectPending.value) return;
  showImportModal.value = false;
  pendingBundle.value = null;
  importResultMessage.value = null;
}

function triggerFileSelect() {
  importFileInput.value?.click();
}

async function onImportFileChosen(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;
  try {
    const text = await file.text();
    const parsed = JSON.parse(text) as ProjectExportBundleView;
    pendingBundle.value = parsed;
    importProjectName.value = parsed.projectName ?? "";
  } catch {
    alertStore.addError("Import failed", "Selected file is not a valid project export bundle.");
  } finally {
    target.value = ""; // Allow re-selecting the same file later.
  }
}

async function confirmImport() {
  if (!pendingBundle.value) return;
  const trimmedName = importProjectName.value.trim();
  if (!trimmedName) return;
  try {
    const response = await importProjectAsync({
      bundle: pendingBundle.value,
      projectName: trimmedName !== (pendingBundle.value.projectName ?? "") ? trimmedName : undefined,
    });
    const unboundMsg = response.unboundSlots.length > 0
      ? ` ${response.unboundSlots.length} connection slot(s) need binding: ${response.unboundSlots.join(", ")}.`
      : "";
    importResultMessage.value = `Imported as a new project.${unboundMsg}`;
    await refetchProjects();
    // Auto-close after a short pause so the user sees the success state.
    setTimeout(closeImportDialog, 1500);
  } catch {
    // Surfaced via importProjectError in the template.
  }
}

// Combine owned and subscribed products for the filter dropdown
const allProducts = computed(() => {
  const productsMap = new Map();

  products.value.forEach(product => {
    productsMap.set(product.id, product);
  });

  subscribedProducts.value.forEach(product => {
    if (!productsMap.has(product.id)) {
      productsMap.set(product.id, product);
    }
  });

  return Array.from(productsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
});

const searchQuery = ref("");
const selectedProduct = ref(routeProductFilter());
const selectedStatus = ref("all");

function routeProductFilter(): string {
  const raw = route.query.productId;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value ? value : "all";
}

// URL is the source of truth for the product filter. When the user navigates
// here from the nav (e.g. clicking a Product group), seed the dropdown from
// the query. When the user changes the dropdown, push the new filter to the URL
// so the active group stays in sync and the view is bookmarkable.
watch(() => route.query.productId, () => {
  selectedProduct.value = routeProductFilter();
});

// Page title reflects the active product scope. Same string is reused in the breadcrumb
// leaf so they don't disagree. Falls back to "Projects" while the product list is loading
// or if the id doesn't resolve (e.g. stale URL).
const pageTitle = computed(() => {
  if (selectedProduct.value === "all") return "Projects";
  if (selectedProduct.value === CUSTOM_PRODUCT_KEY) return "Custom Projects";
  const product = allProducts.value.find(p => p.id === selectedProduct.value);
  return product ? `${product.name} Projects` : "Projects";
});

watch(selectedProduct, (value) => {
  const current = routeProductFilter();
  if (value === current) return;
  const nextQuery = { ...route.query };
  if (value === "all") {
    delete nextQuery.productId;
  } else {
    nextQuery.productId = value;
  }
  router.replace({ path: route.path, query: nextQuery });
});

// Convert to Product modal state
const showConvertModal = ref(false);
const selectedProjectForConversion = ref<Project | null>(null);

const tableOptions = {
  // Paging is off — we're rendering all rows because the dashboard's own controls (status /
  // product / search filters) are how users narrow down. DataTables' default `paging: true` +
  // `pageLength: 10` was silently truncating the list once we passed 10 projects, because we'd
  // also stripped the pagination controls below.
  paging: false,
  // DataTables owns the grid's render order, not the array sort in filteredProjects. Col 1 = name.
  order: [[1, 'asc']],
  columns: [
    { data: 'id', title: 'ID', visible: false },
    {
      data: 'name',
      title: 'Project Name',
      // Lower-case for sorting only — DataTables' default string sort is ordinal ('MC99' before 'Mailchimp').
      render: (data: string, type: string) =>
        type === 'sort' || type === 'type' ? (data ?? '').toLowerCase() : data,
    },
    { data: 'projectType', title: 'Type' },
    { data: 'workflowCount', title: 'Workflows', className: 'dt-bodys-right' }, //datatables automatically aligns types a certain way, so forcing left align for numbers
    {
      data: 'createdAt',
      title: 'Created',
      render: (data: any) => new Date(data).toLocaleDateString()
    },
    {
      data: 'updatedAt',
      title: 'Updated',
      render: (data: any) => new Date(data).toLocaleDateString()
    }
  ],
  layout: {
    topEnd: '', //remove default search box
    bottomEnd: '', //remove default pagination info
    bottomStart: '' //remove default pagination controls
  }
} as Config;


const filteredProjects = computed(() => {
  let filtered = projects.value;

  if (searchQuery.value) {
    filtered = filtered.filter((project) =>
      project.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  if (selectedProduct.value === CUSTOM_PRODUCT_KEY) {
    filtered = filtered.filter(
      (project) => !project.subscriberProductId && !project.publisherProductId
    );
  } else if (selectedProduct.value !== "all") {
    filtered = filtered.filter(
      (project) =>
        project.subscriberProductId === selectedProduct.value ||
        project.publisherProductId === selectedProduct.value
    );
  }

  if (selectedStatus.value !== "all") {
    filtered = filtered.filter(
      (project) =>
        getProjectStatus(project).toLowerCase() === selectedStatus.value
    );
  }

  // Sort case-insensitively. Cosmos's server-side OrderBy is ordinal — "MC99" lands before
  // "Mailchimp" because uppercase 'C' < lowercase 'a' in code-point order. That's mathematically
  // sorted but reads as scrambled. The locale comparator below matches what users expect.
  // Also overlay the live workflow count from the parallel endpoint so the table column reflects
  // it (the response's own `workflowCount` is the broken denormalised value).
  return [...filtered]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }))
    .map((project) => ({
      ...project,
      workflowCount: workflowCountsByProject.value.get(project.id) ?? 0,
    }));
});

// function isPublisherMasterProject(project: Project): boolean {
//   if (!project.subscriberProductId || project.subscriberProductId === "") return false;
//   const product = products.value.find((p) => p.id === project.subscriberProductId);
//   return product != null;
// }

function isPublisherMasterProject(project: Project): boolean {
  return project.projectType === "PublisherMaster";
}

function isProductlessProject(project: Project): boolean {
  if (project.projectType === "DIY") return true;
  return (!project.subscriberProductId || project.subscriberProductId === "") &&
    (!project.publisherProductId || project.publisherProductId === "");
}

function projectProductName(project: Project): string {
  if (isProductlessProject(project)) return "";
  const targetId = project.subscriberProductId || project.publisherProductId;
  // Subscriber orgs won't have the product in `products` (which is the org's *published* products),
  // so also look it up in the subscribed-products list before falling back to "Unknown Product".
  const product =
    products.value.find((p) => p.id === targetId) ??
    subscribedProducts.value.find((p) => p.id === targetId);
  return product ? product.name : "Unknown Product";
}

// Status display is centralised in projectStatus.ts so the dashboard and the detail view stay
// in sync. Readiness is fetched separately via useProjectsReadiness — until that resolves, the
// lookup returns undefined and the badge falls back to lifecycle-only. Once the status query
// lands, "Setup Required" badges appear on projects that aren't run-ready.
function getProjectStatus(project: Project) {
  const isReady = readinessByProject.value.get(project.id);
  return resolveProjectStatus(project, isReady);
}

function getProjectMetadata(project: Project) {
  return {
    // Live count from the parallel endpoint. Falls back to 0 (not `project.workflowCount`)
    // because the list payload's count is the broken denormalised counter — see
    // useProjectsWorkflowCounts.
    workflowCount: workflowCountsByProject.value.get(project.id) ?? 0,
    // Most-recent run from the parallel last-runs endpoint. Absent = never executed (e.g. authoring
    // PublisherMaster projects, which don't run workflows — see useProjectsLastRuns).
    lastRun: formatLastRun(lastRunByProject.value.get(project.id)),
  };
}

// Compact relative time for the dashboard cards; falls back to "Never" when the project has no runs.
function formatLastRun(iso: string | undefined): string {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Live workflow count for direct template lookups (e.g. the table column / list-row badge).
function workflowCountFor(project: Project): number {
  return workflowCountsByProject.value.get(project.id) ?? 0;
}

async function showProject(projectId: string) {
  await router.push(`/projects/${projectId}`);
}

async function createNewProject() {
  // Carry the active product filter through so the new-project form can preselect it.
  // "all" and the custom (productless) bucket have no concrete product to seed.
  const query: Record<string, string> = {};
  if (
    selectedProduct.value !== "all" &&
    selectedProduct.value !== CUSTOM_PRODUCT_KEY
  ) {
    query.productId = selectedProduct.value;
  }
  await router.push({ path: "/projects/new", query });
}

function showConvertToProductModal(project: Project) {
  selectedProjectForConversion.value = project;
  showConvertModal.value = true;
}

function onProjectConverted(_productId: string) {
  invalidateProjects();
  invalidateProducts();
}

const { deleteProjectAsync, deleteProjectPending } = useDeleteProject();

const projectToDelete = ref<Project | null>(null);

function requestDelete(project: Project) {
  projectToDelete.value = project;
}

async function confirmDelete() {
  const project = projectToDelete.value;
  if (!project || deleteProjectPending.value) return;
  try {
    await deleteProjectAsync(project.id);
    projectToDelete.value = null;
  } catch (e) {
    // 409 = blocked (in-flight runs / published product / active schedules).
    projectToDelete.value = null;
    const message = (e as { userMessage?: string })?.userMessage ?? "Unable to delete this project.";
    alertStore.addError("Can't delete project", message);
  }
}

function openRecycleBin() {
  router.push("/projects/recycle-bin");
}

const isDataReady = computed(() => {
  return !loading.value && projects.value.length > 0;
});

function retryLoadProjects() {
  refetchProjects();
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
    <PageHeader
      :title="pageTitle"
      :breadcrumbs="[
        { label: 'Dashboard', href: '/' },
        { label: pageTitle }
      ]"
    >
      <template #actions>
        <button
          class="bb-btn-secondary-cta"
          @click="openRecycleBin"
          title="View and restore deleted projects"
        ><i class="bi bi-trash"></i><span class="d-none d-md-inline ms-1">Recycle Bin</span>
        </button>
        <button
          class="bb-btn-secondary-cta"
          @click="openImportDialog"
          title="Import a project from an export bundle"
        ><i class="bi bi-upload"></i> Import
        </button>
        <button
          class="bb-btn-primary-cta"
          @click="createNewProject"
          data-cy="new-project"
        ><i class="bi bi-plus"></i> New Project
        </button>
      </template>
    </PageHeader>

    <!-- Phase 5: import a project from an exported JSON bundle. -->
    <input
      ref="importFileInput"
      type="file"
      accept="application/json,.json"
      class="d-none"
      @change="onImportFileChosen"
    />

    <div
      v-if="showImportModal"
      class="modal fade show d-block"
      tabindex="-1"
      @click.self="closeImportDialog"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title mb-0">Import project</h2>
            <button type="button" class="btn-close" @click="closeImportDialog" :disabled="importProjectPending"></button>
          </div>
          <div class="modal-body">
            <div v-if="!pendingBundle" class="text-muted mb-3">
              Choose an exported project JSON file. Your org must already subscribe to the
              bundle's source product.
            </div>
            <div v-else>
              <p class="text-muted small mb-3">
                Exported {{ new Date(pendingBundle.exportedAt).toLocaleString() }}
                <span v-if="bundleProductName"> · Product <strong>{{ bundleProductName }}</strong></span>
                <span v-else-if="pendingBundle.productId"> · Product <code>{{ pendingBundle.productId }}</code></span>
              </p>

              <div
                v-if="!orgHasAccessToBundleProduct"
                class="alert alert-warning small mb-3"
              >
                <i class="bi bi-exclamation-triangle me-1"></i>
                Your org doesn't appear to own or subscribe to this bundle's product. The import
                will fail unless you publish it or have an active subscription.
              </div>

              <label class="form-label">Project name</label>
              <input
                v-model="importProjectName"
                type="text"
                class="form-control"
                placeholder="Project name"
                :disabled="importProjectPending"
              />
              <small class="text-muted">Defaults to the exported name. Pick something unique within your org.</small>

              <div v-if="importResultMessage" class="alert alert-success mt-3 mb-0">
                {{ importResultMessage }}
              </div>
              <div v-if="importProjectError" class="alert alert-danger mt-3 mb-0">
                {{ importProjectError.userMessage }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              v-if="!pendingBundle"
              class="bb-btn-primary-cta"
              @click="triggerFileSelect"
            >Choose file</button>
            <template v-else>
              <button class="bb-btn-cancel" @click="closeImportDialog" :disabled="importProjectPending">Cancel</button>
              <button
                class="bb-btn-primary-cta"
                :disabled="!importProjectName.trim() || importProjectPending"
                @click="confirmImport"
              >
                <span v-if="importProjectPending" class="spinner-border spinner-border-sm me-1" role="status"></span>
                Import
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="projects-filters mb-4">
      <div class="row g-3">
        <div class="col-md-6 col-xl-7">
          <input
            type="text"
            class="form-control search-input"
            placeholder="Search projects..."
            v-model="searchQuery"
            data-cy="search-projects"
          />
        </div>
        <div class="col-md-2">
          <select
            class="form-select filter-select"
            v-model="selectedProduct"
            data-cy="filter-product"
          >
            <option value="all">All Products</option>
            <option :value="CUSTOM_PRODUCT_KEY">Custom (no product)</option>
            <option
              v-for="product in allProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <select
            class="form-select filter-select"
            v-model="selectedStatus"
            data-cy="filter-status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="setup required">Setup Required</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Skeleton / Error -->
    <SkeletonLoader v-if="loading || error" type="grid" :cards="6" :grid-columns="4" :error="error" @retry="retryLoadProjects" />

    <!-- DataView -->
    <DataView
      v-else
      :data="filteredProjects"
      :view-mode="'grid'"
      :options="tableOptions"
      @row-click="(project: Project) => showProject(project.id)"
    >
      <!-- Custom card template for grid view -->
      <template #grid-card="{ row: project   }">
        <div
          class="card h-100 card-hover"
          :data-cy="`project-card-${project.id}`"
        >
          <div class="card-body d-flex flex-column">

            <!-- header: title + status badge -->
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h2 class="card-title mb-0 h3">{{ project.name }}</h2>
              <span class="badge" :class="getProjectStatusClass(getProjectStatus(project))">
                {{ getProjectStatus(project) }}
              </span>
            </div>

            <!-- Product label - hidden for productless DIY projects -->
             <p
              class="card-text text-muted d-flex align-items-start gap-2"
              :class="{ 'invisible': isProductlessProject(project) }"
            >
                 <i v-if="isPublisherMasterProject(project)" class="bi bi-bookmark-star-fill text-info"></i>
<!--              <span v-if="isPublisherMasterProject(project)" class="badge bg-gradient publisher-master-badge" >-->
<!--                Publisher Master-->
<!--              </span>-->
              <span>{{ projectProductName(project) }}</span>
            </p>

            <!-- Meta footer: stacked last-run / workflow count on the left, actions menu on the
                 right. Menu carries what used to be footer buttons (Run History / Builder). -->
            <div class="mt-auto d-flex justify-content-between align-items-end">
              <div class="text-muted small">
                <div>{{getProjectMetadata(project).workflowCount}} workflows</div>
                <div>Last run: {{ getProjectMetadata(project).lastRun }}</div>
              </div>
              <div class="dropdown">
                <button type="button" title="More actions" class="bb-btn-secondary-cta btn-sm dropdown-toggle" data-bs-toggle="dropdown" @click.stop>
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" title="Open Builder" href="#" @click.stop.prevent="router.push(`/projects/${project.id}/builder`)">
                    <i class="bi bi-hammer me-2"></i>Builder
                  </a></li>
                  <li><a class="dropdown-item" title="Workflows" href="#" @click.stop.prevent="router.push(`/projects/${project.id}/workflows`)">
                    <i class="bi bi-diagram-3 me-2"></i>Workflows <span class="badge bg-primary">{{ workflowCountFor(project) }}</span>
                  </a></li>
                  <li><a class="dropdown-item" title="Run History"  href="#" @click.stop.prevent="router.push(`/projects/${project.id}/history`)">
                    <i class="bi bi-clock-history me-2"></i>Run History
                  </a></li>
                  <li><a class="dropdown-item" title="Connections" href="#" @click.stop.prevent="router.push(`/projects/${project.id}/connections`)">
                    <i class="bi bi-plug me-2"></i>Connections
                  </a></li>
                  <li><a class="dropdown-item" title="Data Stores" href="#" @click.stop.prevent="router.push(`/projects/${project.id}/datastores`)">
                    <i class="bi bi-database me-2"></i>Data Stores
                  </a></li>
                  <li><hr class="dropdown-divider" /></li>
                  <li><a class="dropdown-item text-danger" title="Delete project" href="#" @click.stop.prevent="requestDelete(project)">
                    <i class="bi bi-trash me-2"></i>Delete
                  </a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Custom empty state -->
      <template #empty-state>
        <div class="text-center py-5">
          <i class="bi bi-box-seam display-1 text-muted"></i>
          <h2 class="mt-3 text-muted">No projects found</h2>
          <p class="text-muted">Create your first project to get started</p>
          <button class="bb-btn-primary-cta" @click="createNewProject">
            <i class="bi bi-plus"></i> Create Project
          </button>
        </div>
      </template>
    </DataView>

    <!-- Convert to Product Modal -->
    <ConvertToProductModal
      v-model:show="showConvertModal"
      :project="selectedProjectForConversion"
      @converted="onProjectConverted"
    />

    <DeleteProjectModal
      :show="projectToDelete !== null"
      :project-name="projectToDelete?.name ?? ''"
      :pending="deleteProjectPending"
      @confirm="confirmDelete"
      @cancel="projectToDelete = null"
    />
      </PanelCard>
    </PanelGrid>
  </main>


</template>

<style scoped lang="scss">


/* No custom CSS needed - using Bootstrap utilities */
</style>
